import { combineClasses } from "@/lib/utils";
import styles from "./FilterChips.module.scss";

export interface FilterChipOption {
  label: string;
  value: string;
}

interface FilterChipsProps {
  options: FilterChipOption[];
  // `null` means the "All" chip — no filter applied.
  value: string | null;
  onChange: (value: string | null) => void;
  // Pass `null` to drop the "All" chip, e.g. for a required tab switch.
  allLabel?: string | null;
  ariaLabel?: string;
  className?: string;
}

// Single-select, horizontally scrollable filter chips with a leading
// "All" chip. Unlike PillToggleGroup (multi-select, never empty), this is
// for a one-of-N list filter such as status.
const FilterChips = ({
  options,
  value,
  onChange,
  allLabel = "All",
  ariaLabel = "Filter",
  className,
}: FilterChipsProps) => {
  const chips: { label: string; value: string | null }[] =
    allLabel === null
      ? options
      : [{ label: allLabel, value: null }, ...options];

  return (
    <div
      className={combineClasses(styles.chips, className)}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {chips.map((chip) => {
        const isActive = chip.value === value;
        return (
          <button
            key={chip.value ?? "__all"}
            type="button"
            role="radio"
            aria-checked={isActive}
            className={combineClasses(
              styles.chip,
              isActive && styles.chipActive,
            )}
            onClick={() => onChange(chip.value)}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
};

export default FilterChips;
